import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

/* Low-stock badge component */
function Badge({ count }) {
  if (!count || count === 0) return null;
  return (
    <span style={{
      position: 'absolute',
      top: '-4px', right: '-4px',
      minWidth: '16px', height: '16px',
      background: '#ef4444', color: '#fff',
      borderRadius: '8px', fontSize: '9px', fontWeight: '800',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 4px', border: '2px solid #fff',
      lineHeight: 1,
    }}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

const BASE_NAV = [
  { path: '/dashboard', label: 'Dashboard', icon: '🏠' },
  { path: '/stock',     label: 'Stock',     icon: '📦' },
  { path: '/orders',    label: 'Orders',    icon: '📋' },
  { path: '/log',       label: 'Log',       icon: '🕓' },
  { path: '/analytics', label: 'Analytics', icon: '📊' },
  { path: '/users',     label: 'Users',     icon: '👥' },
  { path: '/settings',  label: 'Settings',  icon: '⚙️' },
];

export default function Layout({ children }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { userRole, orgName, currentUser, orgId } = useAuth();
  const [lowStockCount, setLowStockCount] = useState(0);

  /* Live low-stock count */
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, 'products'), where('orgId', '==', orgId));
    return onSnapshot(q, snap => {
      const products = snap.docs.map(d => d.data());
      const low = products.filter(p =>
        p.minStock != null ? p.stock < p.minStock : p.stock < p.moq
      ).length;
      setLowStockCount(low);
    });
  }, [orgId]);

  const avatarColor = (() => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#16a34a', '#0891b2', '#dc2626'];
    let hash = 0;
    const str = currentUser?.email || '';
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  })();

  if (isMobile) {
    return (
      <div style={mobileStyles.shell}>
        {/* Mobile top header */}
        <header style={mobileStyles.topBar}>
          <div style={mobileStyles.topBarBrand}>
            <span style={mobileStyles.topBarIcon}>📦</span>
            <div>
              <div style={mobileStyles.topBarName}>StockSpace</div>
              {orgName && <div style={mobileStyles.topBarOrg}>{orgName}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Bell icon with low stock badge */}
            <Link to="/stock" style={{ textDecoration: 'none', position: 'relative' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%',
                background: lowStockCount > 0 ? '#fef2f2' : '#f8fafc',
                border: `1px solid ${lowStockCount > 0 ? '#fecaca' : '#e2e8f0'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', position: 'relative',
              }}>
                🔔
                <Badge count={lowStockCount} />
              </div>
            </Link>
            <Link to="/settings" style={{ textDecoration: 'none' }}>
              <div style={{ ...mobileStyles.avatar, backgroundColor: avatarColor }}>
                {currentUser?.email?.[0].toUpperCase()}
              </div>
            </Link>
          </div>
        </header>

        {/* Main scrollable content */}
        <main style={mobileStyles.main}>
          <div style={mobileStyles.content} className="fade-in">
            {children}
          </div>
        </main>

        {/* Bottom nav bar */}
        <nav style={mobileStyles.bottomNav}>
          {BASE_NAV.map(item => {
            const active = location.pathname === item.path;
            const showBadge = item.path === '/stock' && lowStockCount > 0;
            return (
              <Link key={item.path} to={item.path} style={{ textDecoration: 'none', flex: 1 }}>
                <div style={{
                  ...mobileStyles.bottomNavItem,
                  ...(active ? mobileStyles.bottomNavItemActive : {}),
                }}>
                  <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <span style={mobileStyles.bottomNavIcon}>{item.icon}</span>
                    {showBadge && <Badge count={lowStockCount} />}
                  </div>
                  <span style={{
                    ...mobileStyles.bottomNavLabel,
                    ...(active ? mobileStyles.bottomNavLabelActive : {}),
                  }}>
                    {item.label}
                  </span>
                  {active && <div style={mobileStyles.activeBar} />}
                </div>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  /* ── Desktop layout ── */
  return (
    <div style={desktopStyles.shell}>
      <aside style={desktopStyles.sidebar}>
        <div style={desktopStyles.brand}>
          <span style={desktopStyles.brandIcon}>📦</span>
          <div>
            <div style={desktopStyles.brandName}>StockSpace</div>
            <div style={desktopStyles.orgName}>{orgName || '...'}</div>
          </div>
        </div>

        <nav style={desktopStyles.nav}>
          {BASE_NAV.map(item => {
            const active = location.pathname === item.path;
            const showBadge = item.path === '/stock' && lowStockCount > 0;
            return (
              <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                <div style={{ ...desktopStyles.navItem, ...(active ? desktopStyles.navItemActive : {}) }}
                  className="card-hover">
                  <div style={{ position: 'relative', display: 'inline-flex', width: '20px', textAlign: 'center' }}>
                    <span style={desktopStyles.navIcon}>{item.icon}</span>
                    {showBadge && <Badge count={lowStockCount} />}
                  </div>
                  <span style={{ ...desktopStyles.navLabel, ...(active ? desktopStyles.navLabelActive : {}) }}>
                    {item.label}
                  </span>
                  {active && <div style={desktopStyles.activeDot} />}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bell summary in sidebar bottom */}
        {lowStockCount > 0 && (
          <Link to="/stock" style={{ textDecoration: 'none', padding: '0 10px', marginBottom: '8px', display: 'block' }}>
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '8px', padding: '10px 12px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '16px' }}>🚨</span>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#dc2626' }}>{lowStockCount} low stock item{lowStockCount > 1 ? 's' : ''}</div>
                <div style={{ fontSize: '11px', color: '#ef4444' }}>Tap to restock</div>
              </div>
            </div>
          </Link>
        )}

        <div style={desktopStyles.sidebarBottom}>
          <Link to="/settings" style={{ textDecoration: 'none' }}>
            <div style={{ ...desktopStyles.userInfo, cursor: 'pointer', transition: 'background-color 0.15s ease' }}
              className="card-hover">
              <div style={{ ...desktopStyles.avatar, backgroundColor: avatarColor }}>
                {currentUser?.email?.[0].toUpperCase()}
              </div>
              <div style={desktopStyles.userDetails}>
                <div style={desktopStyles.userEmail}>{currentUser?.email}</div>
                <div style={desktopStyles.userRole}>{userRole} · Settings</div>
              </div>
            </div>
          </Link>
        </div>
      </aside>

      <main style={desktopStyles.main}>
        <div style={desktopStyles.content} className="fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}

const mobileStyles = {
  shell: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  topBarBrand: { display: 'flex', alignItems: 'center', gap: '8px' },
  topBarIcon: { fontSize: '22px' },
  topBarName: { fontSize: '15px', fontWeight: '700', color: '#1e293b', lineHeight: 1.2 },
  topBarOrg: { fontSize: '10px', color: '#94a3b8', fontWeight: '500' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  main: { flex: 1, overflow: 'auto', paddingBottom: '64px' },
  content: { padding: '16px' },
  bottomNav: { display: 'flex', flexDirection: 'row', position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -2px 8px rgba(0,0,0,0.06)', zIndex: 100, height: '60px' },
  bottomNavItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6px 4px 4px', position: 'relative', gap: '2px' },
  bottomNavItemActive: { backgroundColor: '#eff6ff' },
  bottomNavIcon: { fontSize: '18px', lineHeight: 1 },
  bottomNavLabel: { fontSize: '9px', fontWeight: '500', color: '#94a3b8', lineHeight: 1 },
  bottomNavLabelActive: { color: '#3b82f6', fontWeight: '700' },
  activeBar: { position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px', borderRadius: '0 0 2px 2px', backgroundColor: '#3b82f6' },
};

const desktopStyles = {
  shell: { display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' },
  sidebar: { width: '240px', minWidth: '240px', backgroundColor: '#ffffff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', boxShadow: '2px 0 8px rgba(0,0,0,0.04)' },
  brand: { display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 16px', borderBottom: '1px solid #f1f5f9' },
  brandIcon: { fontSize: '28px' },
  brandName: { fontSize: '16px', fontWeight: '700', color: '#1e293b', lineHeight: 1.2 },
  orgName: { fontSize: '11px', color: '#94a3b8', fontWeight: '500', marginTop: '2px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  nav: { flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', position: 'relative', transition: 'all 0.15s ease' },
  navItemActive: { backgroundColor: '#eff6ff' },
  navIcon: { fontSize: '16px', width: '20px', textAlign: 'center' },
  navLabel: { fontSize: '14px', fontWeight: '500', color: '#64748b' },
  navLabelActive: { color: '#3b82f6', fontWeight: '600' },
  activeDot: { position: 'absolute', right: '10px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#3b82f6' },
  sidebarBottom: { padding: '12px', borderTop: '1px solid #f1f5f9' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '8px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  userDetails: { overflow: 'hidden' },
  userEmail: { fontSize: '11px', color: '#475569', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' },
  main: { flex: 1, overflow: 'auto' },
  content: { padding: '28px 32px', maxWidth: '1400px' },
};